import SearchTools from "./SearchTools.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import DefaultTemplate from "../../layout/content-templates/default/DefaultTemplate.tsx";
import OffScreen from "../../../../common/core-ui/OffScreen.tsx";
import { useEffect, useState } from "react";
import NoResultsTemplate from "../common/NoResultsTemplate.tsx";
import styles from "./Search.module.scss";
import { SearchOutletContext } from "./outletContext.ts";
import PaddedDotLoader from "../common/PaddedDotLoader.tsx";
import StartScreen from "../start-screen/StartScreen.tsx";
import { getSearchQuery } from "../query.ts";
import { HomeOutletContext } from "../../layout/useHomeOutletContext.ts";
import { useHomeOutletContext } from "../../layout/useHomeOutletContext.ts";
import useRecentSearches from "../typeahead/useRecentSearches.ts";
import { SearchLink } from "../typeahead/types.ts";
import PageTitle from "../../common/page-title/PageTitle.tsx";
import useContentResults from "../results/useContentResults.ts";
import useTitleResults from "../results/useTitleResults.ts";
import { deferFn } from "../../../../common/utils/function.ts";

function NoResults({ query }: { query: string }) {
  return (
    <NoResultsTemplate
      useStoreLink
      className={styles.noResults}
      title={
        <FormattedMessage
          defaultMessage="We couldn’t find a match for “{query}”"
          values={{ query }}
        />
      }
      message={
        <FormattedMessage defaultMessage="Double-check your spelling, or try using a different search term!" />
      }
    />
  );
}

function SearchInner({ recentSearches }: { recentSearches: SearchLink[] }) {
  const [urlParams] = useSearchParams();
  const query = getSearchQuery(urlParams);
  const { isSidebarExpandable, onSidebarToggle } = useHomeOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const titleResults = useTitleResults();
  const contentResults = useContentResults(urlParams.toString());
  // when no title results and switching to content, reduces flash of empty title results
  const [waitForTabFlip, setWaitForTabFlip] = useState(true);

  /**
   * Normal patterns suggest putting this in a loader function, but that doesn't
   * work here because some searches may be very slow and there are different
   * UI treatments for no results anywhere vs no results in a particular tab
   *
   * useFetchers in the children also wasn't ideal because they clear out
   * once the action completes and data has been revalidated
   *
   * Deferred loaders are also not ideal because it's one per route and iirc
   * doesn't refetch on query change
   *
   */

  // titles normally comes in first so prioritize that if it has results
  // if it comes in empty or if titles comes in second we need to wait
  // for both to land cleanly on the content tab if that has results and titles doesn't
  const isReady =
    titleResults.status === "has-results" ||
    (titleResults.status !== "loading" && contentResults.status !== "loading");

  useEffect(() => {
    // an empty titles mean we flip to the content tab
    if (
      isReady &&
      titleResults.status === "empty" &&
      contentResults.status === "has-results"
    ) {
      navigate(`/search/book-content?${urlParams.toString()}`);
    } else if (isReady) {
      setWaitForTabFlip(false);
    }
  }, [
    contentResults.status,
    isReady,
    navigate,
    titleResults.status,
    urlParams,
  ]);

  useEffect(() => {
    if (waitForTabFlip && isReady) {
      if (location.pathname === "/search/book-content") {
        // used to prevent a micro flash of empty titles
        deferFn(() => setWaitForTabFlip(false));
      }
    }
  }, [location.pathname, isReady, waitForTabFlip]);

  function getMainContent() {
    if (!query) {
      return <StartScreen />;
    }

    if (titleResults.status === "empty" && contentResults.status === "empty") {
      return <NoResults query={query} />;
    }

    if (isReady) {
      return (
        <>
          <div className={styles.tabRow}>
            <NavLink
              to={`/search/titles?${urlParams.toString()}`}
              className="vst-tab"
            >
              <FormattedMessage defaultMessage="Titles" />
            </NavLink>
            <NavLink
              to={`/search/book-content?${urlParams.toString()}`}
              className="vst-tab"
            >
              <FormattedMessage defaultMessage="Book Content" />
            </NavLink>
          </div>
          <Outlet
            context={
              {
                query,
                isSidebarExpandable,
                onSidebarToggle,
                titleResults,
                contentResults,
                waitForTabFlip,
              } satisfies SearchOutletContext & HomeOutletContext
            }
          />
        </>
      );
    }

    return (
      <>
        <PaddedDotLoader aria-hidden={true} />
        <div className={styles.loadingTitle}>
          <FormattedMessage defaultMessage="Loading Results" />
        </div>
        <div className={styles.loadingMessage}>
          <FormattedMessage defaultMessage="This may take a few moments" />
        </div>
      </>
    );
  }

  return (
    <>
      <SearchTools query={query} recentSearches={recentSearches} />
      <div className={styles.resultsArea}>{getMainContent()}</div>
    </>
  );
}

export default function Search() {
  const [urlParams] = useSearchParams();
  const query = getSearchQuery(urlParams);
  // needs to be here to be persistent, see below about full UI reset
  const recentSearches = useRecentSearches();
  const intl = useIntl();
  const title = intl.formatMessage({ defaultMessage: "Search" });

  // We're using the DefaultTemplate in order to be able to use the panel
  return (
    <>
      <DefaultTemplate title={title} noInset headerConfig={{ theme: "accent" }}>
        <OffScreen>
          <PageTitle title={title} />
        </OffScreen>
        {/* key performs a full UI reset on query change */}
        <SearchInner key={query} recentSearches={recentSearches} />
      </DefaultTemplate>
    </>
  );
}
